import { Trans } from "gatsby-plugin-react-i18next";
import React, { useState } from "react";
import NumberFormat from "react-number-format";
import { CSSTransition } from "react-transition-group";
import styled, { css } from "styled-components";

import { Button, ContentWrapper, TextInputCss } from "../assets/styles/base";
import { breakpoints } from "../constants/breakpoints";
import { colors } from "../constants/colors";
import RatePlaceholder from "./RatePlaceholder";
import useInterestRate from "../hooks/useInterestRate";
import { extendUrlWithSourceVersion, urls } from "../constants/urls";
import GtmService from "../service/gtmService";

const Title = ({ useH1, h1Title }) => {
    if (useH1) return <h1 className="h1">{h1Title ? h1Title : <Trans>Konditionencheck</Trans>}</h1>;
    return (
        <h2 className="h1">
            <Trans>Konditionencheck</Trans>
        </h2>
    );
};

const getMonthlyToPay = (mortgage, rate, term) => {
    let creditSum = -mortgage;
    let rateNum = parseFloat(rate.replace(",", "."));
    let preRate = (rateNum / 1200 / 360) * 365;
    let prePeriod = term * 12;

    return (
        (preRate * creditSum * Math.pow(1 + preRate, prePeriod)) /
        (1 - Math.pow(1 + preRate, prePeriod))
    ).toFixed();
};

const OfferCheck = ({
    useH1,
    h1Title,
    calcCTA,
    defaultTerm,
    defaultMortgage,
    defaultUsersOffer,
    noTopMargin,
    hasGreyBg,
    isRefinancing,
    hasMarginTop,
}) => {
    const [type, setType] = useState("fix");
    const [mortgage, setMortgage] = useState(defaultMortgage);
    const [usersOffer, setUsersOffer] = useState(defaultUsersOffer);
    const [term, setTerm] = useState(defaultTerm);
    const [showFinancingModal, setShowFinancingModal] = useState(false);
    const termOptions = [5, 10, 15, 20, 25, 30, 35, 40];
    const { rate, isLoading } = useInterestRate(mortgage, term, type);
    const placeholder = <RatePlaceholder isLoading={isLoading} />;
    const monthlyToPay = rate === null ? null : getMonthlyToPay(mortgage, rate, term);
    const isBetterOffer = monthlyToPay !== null && monthlyToPay < usersOffer;

    const handleModalOpen = () => {
        document.body.classList.add("modal-open");

        setShowFinancingModal(true);

        GtmService.instance.trackEvent({
            event: "kreditRechnerFinanzierungBeispielClick",
        });
    };

    const handleModalClose = () => {
        document.body.classList.remove("modal-open");
        setShowFinancingModal(false);
    };

    const FinancingExample = () => {
        return (
            <BottomInfo>
                <span className="link" onClick={handleModalOpen} role="presentation">
                    &gt; Finanzierungsbeispiel
                </span>
            </BottomInfo>
        );
    };

    const Content = (
        <StyledContentWrapper hasMarginTop={hasMarginTop} noTopMargin={noTopMargin}>
            <ColRight>
                <Title useH1={useH1} h1Title={h1Title} />
                <Explanation>
                    {!isRefinancing &&
                        "Hast du bereits ein Angebot von deiner Bank erhalten? Lass dich nicht lumpen, vergleich die Konditionen."}
                </Explanation>
                <div className="hideIfSingle">
                    <Label className="color-primary">Monatsrate:</Label>
                    <Result>
                        {monthlyToPay === null ? (
                            placeholder
                        ) : isBetterOffer ? (
                            `${monthlyToPay} €`
                        ) : (
                            <small>Kein besseres Angebot gefunden</small>
                        )}
                    </Result>

                    <Label className="color-primary">
                        <strong>Ersparnis&nbsp;/&nbsp;Monat:</strong>
                    </Label>

                    <Result>
                        {monthlyToPay === null ? placeholder : isBetterOffer ? `${usersOffer - monthlyToPay} €` : "-"}
                    </Result>

                    <p>
                        <a
                            href={extendUrlWithSourceVersion(
                                `${urls.survey}?amount=${mortgage}&term=${term}&interestRateType=${type}&monthlyRate=${usersOffer}&component=konditionencheck`,
                            )}
                        >
                            <Button>{calcCTA ? calcCTA : <Trans>mortgageCalc.calculateBtn</Trans>}</Button>
                        </a>
                    </p>

                    <FinancingExample />
                </div>
            </ColRight>

            <ColLeft>
                <SectionHeader>{isRefinancing ? "Dein aktueller Kredit" : "Angebot deiner Bank"}</SectionHeader>

                <GroupsWrapper>
                    <Group>
                        <Label htmlFor="mortgageLoan">{isRefinancing ? "Restschuld" : "Kreditbetrag"}:</Label>
                        <StyledNumberFormat
                            allowNegative={false}
                            thousandSeparator={"."}
                            decimalSeparator={","}
                            suffix={" €"}
                            /*onFocus={() =>
                                GtmService.instance.trackEvent({
                                    event: "kreditRechnerKaufpreisInput",
                                })
                            }*/
                            onValueChange={(value) => setMortgage(value.floatValue >= 0 ? value.floatValue : 0)}
                            value={mortgage}
                            center="true"
                            color={colors.primary}
                            id="mortgageLoan"
                            pattern="[0-9]*"
                            displayType={"number"}
                        />
                        <Label htmlFor="mortgageTerm">{isRefinancing ? "Restlaufzeit" : "Laufzeit"}:</Label>
                        <TermSelectMobile
                            center
                            id="mortgageTerm"
                            onChange={(e) => setTerm(e.target.value)}
                            onClick={() =>
                                GtmService.instance.trackEvent({
                                    event: "kreditRechnerLaufzeitClick",
                                })
                            }
                            value={term}
                        >
                            {termOptions.map((option, key) => (
                                <option key={key} value={option}>
                                    {option} Jahre
                                </option>
                            ))}
                        </TermSelectMobile>
                    </Group>
                    <Group>
                        <Label>Zinsen:</Label>
                        <TypeWrapper>
                            <TypeBtn
                                isActive={type === "fix" ? true : false}
                                onClick={() => {
                                    setType("fix");

                                    GtmService.instance.trackEvent({
                                        event: "kreditRechnerFixClick",
                                    });
                                }}
                            >
                                Fix
                            </TypeBtn>
                            <TypeBtn
                                isActive={type === "variable" ? true : false}
                                onClick={() => {
                                    setType("variable");

                                    GtmService.instance.trackEvent({
                                        event: "kreditRechnerVariableClick",
                                    });
                                }}
                            >
                                Variabel
                            </TypeBtn>
                        </TypeWrapper>

                        <Label htmlFor="mortgageLoan">
                            <Trans>Monatsrate</Trans>:
                        </Label>
                        <StyledNumberFormat
                            allowNegative={false}
                            thousandSeparator={"."}
                            decimalSeparator={","}
                            suffix={" €"}
                            /*onFocus={() =>
                                GtmService.instance.trackEvent({
                                    event: "kreditRechnerKaufpreisInput",
                                })
                            }*/
                            onValueChange={(value) => setUsersOffer(value.floatValue >= 0 ? value.floatValue : 0)}
                            value={usersOffer}
                            center="true"
                            color={colors.primary}
                            id="mortgageLoan"
                            pattern="[0-9]*"
                            displayType={"number"}
                        />
                    </Group>
                </GroupsWrapper>

                <div className="hideIfSplit">
                    <SectionHeader>Unser miracl Angebot</SectionHeader>
                    <GroupsWrapper>
                        <Group>
                            <Label className="hideIfSplit">Monatsrate:</Label>
                            <Result className="hideIfSplit">
                                {monthlyToPay === null ? (
                                    placeholder
                                ) : isBetterOffer ? (
                                    `${monthlyToPay} €`
                                ) : (
                                    <small>Kein besseres Angebot gefunden</small>
                                )}
                            </Result>
                        </Group>
                        <Group>
                            <Label className="hideIfSplit">
                                <strong>Ersparnis&nbsp;/&nbsp;Monat:</strong>
                            </Label>
                            <Result className="hideIfSplit">
                                {monthlyToPay === null
                                    ? placeholder
                                    : isBetterOffer
                                    ? `${usersOffer - monthlyToPay} €`
                                    : "-"}
                            </Result>
                        </Group>
                    </GroupsWrapper>

                    <CenteredCTA>
                        <a
                            className="hideIfSplit"
                            href={extendUrlWithSourceVersion(
                                `${urls.survey}?amount=${mortgage}&term=${term}&interestRateType=${type}&monthlyRate=${usersOffer}&component=konditionencheck`,
                            )}
                        >
                            <Button>{calcCTA ? calcCTA : <Trans>mortgageCalc.calculateBtn</Trans>}</Button>
                        </a>
                    </CenteredCTA>

                    <FinancingExample />
                </div>
            </ColLeft>

            <CSSTransition in={showFinancingModal} timeout={1300} unmountOnExit>
                <FinancingExample handleModalClose={handleModalClose} />
            </CSSTransition>
        </StyledContentWrapper>
    );
    if (hasGreyBg) return <GreyBg>{Content}</GreyBg>;
    return Content;
};

const media1col = breakpoints.tabletMax;
const media2col = breakpoints.desktopMin;

const GreyBg = styled.div`
    background: #f8f8f8;
`;

const Explanation = styled.div`
    @media (max-width: ${media1col}) {
        margin-top: -10px;
    }
`;

const CenteredCTA = styled.div`
    margin: 20px 0 30px;
    text-align: center;
`;

const SectionHeader = styled.h3`
    border-top: 2px solid #dcdcff;
    margin: 34px 0 0 0;
    padding-top: 22px;

    @media (min-width: ${media2col}) {
        &:first-child {
            border: 0;
            margin-top: 0;
            padding-top: 0;
        }
    }
`;

const StyledContentWrapper = styled(ContentWrapper)`
    @media (max-width: ${media1col}) {
        .hideIfSingle {
            display: none;
        }
    }
    @media (min-width: ${media2col}) {
        display: flex;
        justify-content: space-between;

        .hideIfSplit {
            display: none;
        }
    }

    ${({ noTopMargin }) =>
        noTopMargin &&
        css`
            margin-top: 0;
        `};
`;

const ColLeft = styled.div`
    @media (min-width: ${media2col}) {
        border: 3px solid #f6f8f7;
        border-radius: 50px;
        box-shadow: 0px 0px 30px #0000001a;
        box-sizing: border-box;
        padding: 60px;
        width: 42%;
    }
    @media (min-width: ${breakpoints.desktopBigMin}) {
        padding-left: 80px;
        padding-right: 80px;
    }
`;

const ColRight = styled.div`
    @media (min-width: ${media2col}) {
        padding-top: 40px;
        width: 52%;

        .h1,
        h2 {
            margin-bottom: 12px;
        }
        p {
            margin: 20px 0 0;
        }
    }
`;

const TypeWrapper = styled.div`
    background: #dbdbfe;
    border-radius: 20px;
    color: #fff;
    display: flex;
    height: 40px;
`;

const TypeBtn = styled.span`
    align-items: center;
    border-radius: 20px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    width: 50%;

    @media (max-width: ${breakpoints.mobileMax}) {
        &:first-child {
            width: 42%;
        }
        &:last-child {
            width: 58%;
        }
    }

    ${({ isActive }) =>
        isActive &&
        css`
            background: ${colors.primary};
        `};
`;

const GroupsWrapper = styled.div`
    @media (max-width: ${media1col}) {
        display: flex;
    }
`;

const Group = styled.div`
    @media (max-width: ${media1col}) {
        width: 50%;

        &:first-child {
            margin-right: 10%;
        }
    }
`;

const Label = styled.label`
    display: block;
    margin: 30px 0 5px;
    position: relative;

    @media (max-width: ${media1col}) {
        padding-left: 20px;
    }
    @media (min-width: ${media2col}) {
        &.color-primary {
            margin-bottom: 0;
        }
    }
`;

const StyledNumberFormat = styled(NumberFormat)`
    ${TextInputCss};

    @media (min-width: ${media2col}) {
        border-radius: 23px;
        height: 46px;
    }
`;

const Result = styled.div`
    color: ${colors.primary};
    font-size: 26px;
    font-weight: 500;

    @media (max-width: ${media1col}) {
        padding-left: 20px;
    }
    @media (min-width: ${media2col}) {
        font-size: 36px;
        margin-bottom: 15px;
    }
`;

const TermSelectMobile = styled.select`
    ${TextInputCss};

    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    background: #fff;
    color: ${colors.primary};

    /* start fix safari not centering select */
    padding-right: 0;
    padding-left: calc(50% - 37px);
    text-align: left;
    /* end safari fix */

    @media (min-width: ${media2col}) {
        border-radius: 23px;
        height: 46px;
    }
`;

const BottomInfo = styled.div`
    border-top: 2px solid #dcdcff;
    font-size: 11px;
    margin: 45px 0 25px;
    padding: 7px 5px;

    @media (min-width: ${media2col}) {
        font-size: 15px;
        line-height: 1.6;
        margin-top: 45px;
        padding-top: 12px;
    }
`;

OfferCheck.defaultProps = {
    useH1: false,
    defaultTerm: 35,
    defaultMortgage: 320000,
    defaultUsersOffer: 1020,
    h1Title: "",
    hasGreyBg: false,
};

export default OfferCheck;
